import React from "react"
import { useTranslation } from "react-i18next"
import { PPDefaultSelector } from "../ui/PPDefaultSelector"

export default function PPDematchingToolbar({
    shopOptions,
    selectedShop,
    onShopChange,
    shopsLoading,
    displayPage,
    totalPages,
    totalCount,
    page,
    onPageChange,
    jumpInput,
    onJumpInputChange,
    onJump,
    pairsLoading,
    filtersExpanded,
    producerOptions,
    selectedProducer,
    onProducerChange,
    producersLoading,
    categoryOptions,
    selectedCategory,
    onCategoryChange,
    categoriesLoading,
    filterReady,
    sortByPrice,
    onSortByPriceChange,
    onlyRemoved,
    onOnlyRemovedChange
}) {
    const [t] = useTranslation()

    return (
        <section className="pp-matching-toolbar mb-3">
            <div className="pp-matching-toolbar__primary">
                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--shop">
                    <label className="pp-matching-toolbar__label" htmlFor="dematching-shop">
                        {t("dematching.shop")}
                    </label>
                    <PPDefaultSelector
                        id="dematching-shop"
                        data={shopOptions}
                        value={selectedShop}
                        onChange={(opt) => onShopChange(opt?.value ?? null)}
                        isClearable={false}
                        isDisabled={shopsLoading || shopOptions.length === 0}
                        menuInPortal
                    />
                </div>

                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--nav">
                    <span className="pp-matching-toolbar__label">
                        {t("dematching.page_of", { current: displayPage, total: totalPages, count: totalCount })}
                    </span>
                    <div className="pp-matching-nav">
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={() => onPageChange(0)}
                            disabled={page <= 0 || pairsLoading}
                            aria-label={t("dematching.first_page")}
                        >
                            <i className="fas fa-angle-double-left" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={() => onPageChange(Math.max(0, page - 1))}
                            disabled={page <= 0 || pairsLoading}
                            aria-label={t("dematching.prev_page")}
                        >
                            <i className="fas fa-chevron-left" />
                        </button>
                        <form className="pp-matching-nav-jump" onSubmit={onJump}>
                            <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="form-control form-control-sm pp-matching-nav-input text-center"
                                value={jumpInput}
                                onChange={onJumpInputChange}
                                disabled={totalPages <= 1 || pairsLoading}
                                aria-label={t("dematching.jump_input")}
                            />
                            <button
                                type="submit"
                                className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                                disabled={pairsLoading || totalPages <= 1}
                                title={t("dematching.jump_title")}
                            >
                                {t("dematching.jump")}
                            </button>
                        </form>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
                            disabled={page + 1 >= totalPages || pairsLoading}
                            aria-label={t("dematching.next_page")}
                        >
                            <i className="fas fa-chevron-right" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-nav__btn"
                            onClick={() => onPageChange(totalPages - 1)}
                            disabled={page + 1 >= totalPages || pairsLoading}
                            aria-label={t("dematching.last_page")}
                        >
                            <i className="fas fa-angle-double-right" />
                        </button>
                    </div>
                </div>

                <div className="pp-matching-toolbar__field pp-matching-toolbar__field--filters">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm pp-matching-toolbar__filter-btn"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseDematchingFilter"
                        aria-expanded={filtersExpanded}
                        aria-controls="collapseDematchingFilter"
                    >
                        <i className="fas fa-filter me-1" aria-hidden="true" />
                        {t("dematching.more_filters")}
                        <i
                            className={
                                "fas ms-1 pp-matching-filter-toggle-icon " +
                                (filtersExpanded ? "fa-angle-up" : "fa-angle-down")
                            }
                            aria-hidden="true"
                        />
                    </button>
                </div>
            </div>

            <div className="collapse pp-matching-toolbar__filters-collapse" id="collapseDematchingFilter">
                <div className="pp-matching-toolbar__filters">
                    <div className="pp-matching-toolbar__filters-grid pp-matching-toolbar__filters-grid--dematching">
                        <div className="pp-matching-toolbar__filters-field">
                            <label className="pp-matching-toolbar__label">{t("dematching.category")}</label>
                            <PPDefaultSelector
                                id="dematching-category"
                                data={categoryOptions}
                                value={selectedCategory}
                                onChange={(opt) => onCategoryChange(opt?.value ?? null)}
                                isDisabled={categoriesLoading || !filterReady}
                                placeholder={t("dematching.all_categories")}
                                menuInPortal
                            />
                        </div>
                        <div className="pp-matching-toolbar__filters-field">
                            <label className="pp-matching-toolbar__label">{t("dematching.producer")}</label>
                            <PPDefaultSelector
                                id="dematching-producer"
                                data={producerOptions}
                                value={selectedProducer}
                                onChange={(opt) => onProducerChange(opt?.value ?? null)}
                                isDisabled={producersLoading || !filterReady}
                                placeholder={t("dematching.all_producers")}
                                menuInPortal
                            />
                        </div>
                        <div className="pp-matching-toolbar__filters-field">
                            <label className="pp-matching-toolbar__label">{t("dematching.only_removed")}</label>
                            <div className="form-check mt-1">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="dematching-only-removed"
                                    checked={onlyRemoved}
                                    onChange={(e) => onOnlyRemovedChange(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="dematching-only-removed">
                                    {t("dematching.only_removed_hint")}
                                </label>
                            </div>
                        </div>
                        <div className="pp-matching-toolbar__filters-field">
                            <label className="pp-matching-toolbar__label">{t("dematching.sort_by_price")}</label>
                            <div className="form-check mt-1">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="dematching-sort-price"
                                    checked={sortByPrice}
                                    onChange={(e) => onSortByPriceChange(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="dematching-sort-price">
                                    {t("dematching.sort_by_price_hint")}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
